import React, { useEffect, useState } from "react";
import pdfjsLib from "@/lib/pdfWorker";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, ZoomIn, ZoomOut } from "lucide-react";

export default function QuestionPaperViewer({ paper, open, onOpenChange }) {
  const [viewportElement, setViewportElement] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState("");
  const [zoom, setZoom] = useState(1);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (!open || !paper) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setPdf(null);
    setPages([]);
    setZoom(1);

    api.get(`/question-papers/${paper.id}/question-pdf`, { responseType: "arraybuffer" })
      .then((res) => pdfjsLib.getDocument({ data: res.data }).promise)
      .then((document) => { if (!cancelled) setPdf(document); })
      .catch(() => { if (!cancelled) setError("Question paper PDF is not available."); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [open, paper]);

  useEffect(() => {
    if (!open || !viewportElement) return;
    setWidth(Math.floor(viewportElement.getBoundingClientRect().width));
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    observer.observe(viewportElement);
    return () => observer.disconnect();
  }, [open, viewportElement]);

  useEffect(() => {
    if (!pdf || !width) return;
    let cancelled = false;
    const renderPages = async () => {
      const rendered = [];
      setRendering(true);
      try {
        for (let number = 1; number <= pdf.numPages; number += 1) {
          const page = await pdf.getPage(number);
          const base = page.getViewport({ scale: 1 });
          const cssWidth = Math.max(280, Math.min(width - 16, 900) * zoom);
          const outputScale = Math.min(window.devicePixelRatio || 1, 2);
          const viewport = page.getViewport({ scale: (cssWidth / base.width) * outputScale });
          const canvas = document.createElement("canvas");
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
          rendered.push({ src: canvas.toDataURL("image/jpeg", 0.9), width: cssWidth, number });
        }
        if (!cancelled) setPages(rendered);
      } catch {
        if (!cancelled) setError("The question paper could not be displayed.");
      } finally {
        if (!cancelled) setRendering(false);
      }
    };
    renderPages();
    return () => { cancelled = true; };
  }, [pdf, width, zoom]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[100dvh] w-screen max-w-none flex-col gap-0 border-0 p-0 sm:h-[94vh] sm:w-[94vw] sm:max-w-6xl sm:rounded-lg">
        <DialogHeader className="shrink-0 border-b border-border py-3 pl-4 pr-16 text-left sm:pl-5 sm:pr-20">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <DialogTitle className="truncate">{paper?.title || "Question paper"}</DialogTitle>
              <DialogDescription className="truncate">Read-only question paper</DialogDescription>
            </div>
            <div className="mr-1 flex shrink-0 items-center gap-1 sm:mr-0" aria-label="Zoom controls">
              <Button type="button" variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setZoom((z) => Math.max(0.75, z - 0.25))} disabled={zoom <= 0.75} aria-label="Zoom out"><ZoomOut className="h-4 w-4" /></Button>
              <span className="w-11 text-center text-xs text-muted-foreground">{Math.round(zoom * 100)}%</span>
              <Button type="button" variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setZoom((z) => Math.min(2, z + 0.25))} disabled={zoom >= 2} aria-label="Zoom in"><ZoomIn className="h-4 w-4" /></Button>
            </div>
          </div>
        </DialogHeader>
        <div ref={setViewportElement} className="min-h-0 flex-1 overflow-auto bg-muted/60 p-2 sm:p-4" data-testid="question-paper-viewer">
          {(loading || rendering) && pages.length === 0 && <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /> Loading question paper…</div>}
          {error && <div className="flex h-full items-center justify-center px-6 text-center text-sm text-destructive">{error}</div>}
          {!error && <div className="mx-auto flex min-w-max flex-col items-center gap-3 sm:gap-4">
            {pages.map((page) => <img key={page.number} src={page.src} width={page.width} alt={`Question paper page ${page.number}`} draggable="false" className="select-none bg-white shadow-sm" onContextMenu={(event) => event.preventDefault()} />)}
          </div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
